import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { apiJson } from "../lib/queryClient";
import TrackMap, { type TrackPoint } from "../components/TrackMap";

type DjiLogMeta = {
  status: "parsed" | "undecryptable" | "error";
  fileName: string;
  objectPath: string;
  sizeBytes?: number;
  uploadedAt?: string;
  parsedAt?: string;
  version?: number;
  droneName?: string;
  maxAltitudeM?: number;
  durationSec?: number;
  totalDistanceM?: number;
  batteryStartPercent?: number;
  batteryEndPercent?: number;
  batteryMinPercent?: number;
  trackPointCount?: number;
  message?: string;
};

type AnalyzeResult = { meta: DjiLogMeta; track: TrackPoint[] };

type LogbookEntry = {
  id: string;
  droneName: string;
  flightDate: string;
  locationName: string | null;
};

const MAX_DJI_LOG_BYTES = 15 * 1024 * 1024;

export default function DjiAnalyze() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data: entries = [] } = useQuery<LogbookEntry[]>({ queryKey: ["/api/logbook"] });

  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AnalyzeResult | null>(null);

  const [selectedEntryId, setSelectedEntryId] = useState<string>("");
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignDone, setAssignDone] = useState<string | null>(null);

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_DJI_LOG_BYTES) {
      setError("Datei zu groß (max. 15 MB).");
      return;
    }
    setAnalyzing(true);
    setError(null);
    setResult(null);
    setAssignDone(null);
    setAssignError(null);
    setSelectedEntryId("");
    try {
      const { uploadURL } = await apiJson<{ uploadURL: string }>("POST", "/api/objects/upload");
      const put = await fetch(uploadURL, {
        method: "PUT",
        body: file,
        headers: { "Content-Type": "text/plain" },
      });
      if (!put.ok) throw new Error("Upload ins Object Storage fehlgeschlagen");
      const res = await apiJson<AnalyzeResult>("POST", "/api/dji-log/analyze", {
        uploadURL,
        fileName: file.name,
        sizeBytes: file.size,
      });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fehler beim Auswerten");
    } finally {
      setAnalyzing(false);
    }
  }

  async function handleAssign(target: "existing" | "new") {
    if (!result) return;
    if (target === "existing" && !selectedEntryId) {
      setAssignError("Bitte einen Flug auswählen.");
      return;
    }
    setAssigning(true);
    setAssignError(null);
    setAssignDone(null);
    try {
      const created = await apiJson<{ id: string }>("POST", "/api/dji-log/assign", {
        objectPath: result.meta.objectPath,
        fileName: result.meta.fileName,
        sizeBytes: result.meta.sizeBytes,
        target,
        entryId: target === "existing" ? selectedEntryId : undefined,
      });
      await queryClient.invalidateQueries({ queryKey: ["/api/logbook"] });
      if (target === "new" && created?.id) {
        navigate(`/logbuch/${created.id}`);
        return;
      }
      navigate(`/logbuch/${selectedEntryId}`);
    } catch (err) {
      setAssignError(err instanceof Error ? err.message : "Zuordnung fehlgeschlagen");
    } finally {
      setAssigning(false);
    }
  }

  const meta = result?.meta;
  const track: TrackPoint[] = result?.track ?? [];
  const firstPoint = track.length > 0 ? track[0] : undefined;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/logbuch" className="text-sm text-brand-600 hover:underline">← Zurück zum Logbuch</Link>
        <h1 className="text-xl font-semibold mt-2">DJI-Log auswerten</h1>
        <p className="text-sm text-[var(--color-app-text-secondary)] mt-1">
          Lade ein DJI-Controller-Flugprotokoll (.txt aus DJI Fly/GO/Pilot) hoch. Die Auswertung
          erscheint sofort — du kannst sie anschließend optional einem Flug zuordnen.
        </p>
      </div>

      <div className="card">
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={analyzing}
          className="btn-primary text-sm"
          data-testid="button-dji-analyze-upload"
        >
          {analyzing ? "Wird ausgewertet …" : result ? "Anderes Protokoll auswerten" : "DJI-Protokoll hochladen"}
        </button>
        {error ? <p className="text-xs text-[var(--color-nogo)] mt-2" data-testid="text-dji-analyze-error">{error}</p> : null}
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt,text/plain"
          onChange={handleFile}
          className="hidden"
          data-testid="input-dji-analyze-file"
        />
      </div>

      {meta ? (
        <>
          <div className="card" data-testid="section-dji-analyze-result">
            <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">Auswertung</h2>
            {meta.status === "parsed" ? (
              <p className="text-xs text-[var(--color-go)] mb-3">
                Protokoll erfolgreich ausgewertet
                {meta.trackPointCount ? ` · ${meta.trackPointCount} GPS-Punkte` : ""}.
              </p>
            ) : meta.status === "undecryptable" ? (
              <p className="text-xs text-[var(--color-caution)] mb-3" data-testid="text-dji-analyze-notice">
                Dieses Protokoll ist verschlüsselt (DJI v13+) und konnte ohne DJI-API-Schlüssel nicht
                vollständig ausgewertet werden. Die Basis-Kennzahlen sind unten dargestellt.
                {meta.message ? ` ${meta.message}` : ""}
              </p>
            ) : (
              <p className="text-xs text-[var(--color-nogo)] mb-3" data-testid="text-dji-analyze-notice">
                Protokoll konnte nicht ausgewertet werden.
                {meta.message ? ` ${meta.message}` : ""}
              </p>
            )}
            <Row label="Datei" value={meta.fileName} />
            {meta.droneName ? <Row label="Drohne (Log)" value={meta.droneName} /> : null}
            {meta.version != null ? <Row label="Log-Version" value={`v${meta.version}`} /> : null}
            {meta.maxAltitudeM != null ? <Row label="Max. Höhe" value={`${meta.maxAltitudeM.toFixed(1)} m`} /> : null}
            {meta.durationSec != null ? <Row label="Flugdauer" value={`${Math.round(meta.durationSec)} s`} /> : null}
            {meta.totalDistanceM != null ? (
              <Row label="Strecke" value={`${(meta.totalDistanceM / 1000).toFixed(2)} km`} />
            ) : null}
            {meta.batteryStartPercent != null || meta.batteryEndPercent != null || meta.batteryMinPercent != null ? (
              <Row
                label="Akku (Start/Ende/Min)"
                value={[
                  meta.batteryStartPercent != null ? `${meta.batteryStartPercent}%` : "—",
                  meta.batteryEndPercent != null ? `${meta.batteryEndPercent}%` : "—",
                  meta.batteryMinPercent != null ? `${meta.batteryMinPercent}%` : "—",
                ].join(" / ")}
              />
            ) : null}
          </div>

          <div className="card" data-testid="section-dji-analyze-track">
            <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">GPS-Track</h2>
            {track.length >= 2 ? (
              <>
                <p className="text-xs text-[var(--color-app-text-secondary)] mb-2">{track.length} Punkte aufgezeichnet</p>
                <TrackMap
                  key={`analyze-track-${track.length}`}
                  track={track}
                  fallbackLat={firstPoint?.lat}
                  fallbackLon={firstPoint?.lon}
                />
              </>
            ) : (
              <p className="text-sm text-[var(--color-app-text-secondary)]">
                Kein GPS-Track verfügbar (verschlüsseltes Log oder keine Positionsdaten).
              </p>
            )}
          </div>

          <div className="card" data-testid="section-dji-analyze-assign">
            <h2 className="font-semibold mb-3 pb-2 border-b border-[var(--color-app-border)]">Einem Flug zuordnen</h2>
            <p className="text-sm text-[var(--color-app-text-secondary)] mb-3">
              Diese Auswertung wird nicht gespeichert, solange du sie keinem Flug zuordnest.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-[var(--color-app-text-secondary)] block mb-1">Bestehendem Flug zuordnen</label>
                <div className="flex gap-2 flex-wrap">
                  <select
                    value={selectedEntryId}
                    onChange={(e) => setSelectedEntryId(e.target.value)}
                    className="border border-[var(--color-app-border)] rounded-md px-2 py-1.5 text-sm flex-1 min-w-[200px]"
                    data-testid="select-dji-assign-entry"
                  >
                    <option value="">— Flug auswählen —</option>
                    {entries.map((e) => (
                      <option key={e.id} value={e.id}>
                        {e.flightDate} · {e.droneName}
                        {e.locationName ? ` · ${e.locationName}` : ""}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleAssign("existing")}
                    disabled={assigning || !selectedEntryId}
                    className="btn-secondary text-sm"
                    data-testid="button-dji-assign-existing"
                  >
                    {assigning ? "Wird zugeordnet …" : "Zuordnen"}
                  </button>
                </div>
              </div>

              <div className="pt-2 border-t border-[var(--color-app-border)]">
                <button
                  onClick={() => handleAssign("new")}
                  disabled={assigning}
                  className="btn-primary text-sm"
                  data-testid="button-dji-assign-new"
                >
                  {assigning ? "Wird gespeichert …" : "Als neuen Flug speichern"}
                </button>
              </div>
            </div>
            {assignError ? (
              <p className="text-xs text-[var(--color-nogo)] mt-2" data-testid="text-dji-assign-error">{assignError}</p>
            ) : null}
            {assignDone ? (
              <p className="text-xs text-[var(--color-go)] mt-2" data-testid="text-dji-assign-done">{assignDone}</p>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-3 gap-2 text-sm py-1">
      <div className="text-[var(--color-app-text-secondary)]">{label}</div>
      <div className="col-span-2 text-[var(--color-app-text)]">{value}</div>
    </div>
  );
}
